import React, { Component } from 'react';
import { Renderer, Drawing, CameraView, type CameraOrbit } from '@hi-level/3d-renderer';
import { Camera, Eye, Film, Maximize, Phone, Video } from 'lucide-react';
import './ThreeContainer.css';
import ShareModal from './ShareModal';

type InputProps = {
  drawing: Drawing;
  uuid: string;
  /**
   * Where to open the camera, when the quotation this link came from named an
   * angle for the floor. A starting position only — everything still turns.
   */
  openAngle?: CameraOrbit | null;
};

type State = {
  buttonsEnabled: boolean;
  showCameraMenu: boolean;
  showVisibilityMenu: boolean;
  cinematic: boolean;
};

export default class ThreeContainer extends Component<InputProps, State> {
  private el: any = null;
  private renderer: any = null;

  constructor(props: InputProps) {
    super(props);
    this.state = {
      buttonsEnabled: false,
      showCameraMenu: false,
      showVisibilityMenu: false,
      cinematic: false,
    };
  }

  componentDidMount() {
    this.renderer = new Renderer({
      containerElement: this.el,
      baseUrl: `${import.meta.env.BASE_URL}models/`,
      enableShadows: true,
      onError: this.onRendererError,
    });
    if (this.renderer.canRender) {
      this.renderer.loadFloor(this.props.drawing);
      // Set before anything is drawn rather than after: loadFloor works out
      // the floor's size as it returns, so pointing the camera now means the
      // model is built facing the right way instead of being framed twice.
      if (this.props.openAngle) {
        this.renderer.setCameraOrbit(this.props.openAngle, false);
      }
      this.renderer.enableKeyboardShortcuts();
      this.setState({ buttonsEnabled: true });
    }
  }

  componentWillUnmount() {
    this.renderer?.destroy();
  }

  onRendererError = (error: unknown) => {
    throw error;
  };

  toggleDeckingClick = () => {
    this.renderer.toggleDecking();
  };

  toggleEdgeProtectionClick = () => {
    this.renderer.toggleEdgeProtection();
  };

  togglePalletGatesClick = () => {
    this.renderer.togglePalletGates();
  };

  toggleStaircasesClick = () => {
    this.renderer.toggleStaircases();
  };

  toggleShadowsClick = () => {
    this.renderer.toggleShadows();
  };

  toggleCinematicClick = () => {
    const next = !this.state.cinematic;
    this.renderer.toggleCinematic(next);
    this.setState({ cinematic: next });
  };

  setCameraView = (view: CameraView) => {
    this.renderer.setCameraView(view);
    this.setState({ showCameraMenu: false });
  };

  fitToViewClick = () => {
    this.renderer.fitToView();
  };

  takeSnapshotClick = () => {
    const imageData = this.renderer.getImageData();
    if (!imageData) return;
    const link = document.createElement('a');
    link.download = 'floor.png';
    link.href = imageData;
    link.click();
  };

  render() {
    const disabled = !this.state.buttonsEnabled;

    return (
      <div className="relative h-full">
        <div
          ref={(element) => {
            this.el = element;
          }}
          className="Renderer"
        />

        {/* Bottom toolbar */}
        <div className="absolute bottom-6 left-1/2 -translate-x-1/2 z-10">
          {/* Popover menus */}
          {this.state.showVisibilityMenu && (
            <div className="flex flex-wrap gap-1 rounded-2xl bg-brand-dark/80 backdrop-blur-md p-2 mb-2 shadow-xl ring-1 ring-white/10 justify-center">
              {(
                [
                  ['Decking', this.toggleDeckingClick],
                  ['Edge protection', this.toggleEdgeProtectionClick],
                  ['Pallet gates', this.togglePalletGatesClick],
                  ['Staircases', this.toggleStaircasesClick],
                  ['Shadows', this.toggleShadowsClick],
                ] as const
              ).map(([label, handler]) => (
                <button
                  key={label}
                  onClick={handler}
                  disabled={disabled}
                  className="rounded-lg px-3 py-1.5 text-xs font-medium text-white/90 hover:bg-white/10 cursor-pointer transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  {label}
                </button>
              ))}
            </div>
          )}
          {this.state.showCameraMenu && (
            <div className="flex flex-wrap gap-1 rounded-2xl bg-brand-dark/80 backdrop-blur-md p-2 mb-2 shadow-xl ring-1 ring-white/10 justify-center">
              {(['top', 'front', 'rear', 'left', 'right', 'isometric'] as CameraView[]).map(
                (view) => (
                  <button
                    key={view}
                    onClick={() => this.setCameraView(view)}
                    disabled={disabled}
                    className="rounded-lg px-3 py-1.5 text-xs font-medium text-white/90 hover:bg-white/10 capitalize cursor-pointer transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    {view}
                  </button>
                ),
              )}
              <IconButton
                onClick={this.fitToViewClick}
                disabled={disabled}
                title="Fit to view"
                icon={<Maximize className="size-4" />}
              />
              <IconButton
                onClick={this.toggleCinematicClick}
                disabled={disabled}
                active={this.state.cinematic}
                title="Cinematic"
                icon={<Film className="size-4" />}
              />
            </div>
          )}

          <div className="flex items-center gap-2 rounded-2xl bg-brand-dark/80 backdrop-blur-md px-3 py-2 shadow-xl ring-1 ring-white/10">
            <IconButton
              onClick={() =>
                this.setState({
                  showVisibilityMenu: !this.state.showVisibilityMenu,
                  showCameraMenu: false,
                })
              }
              disabled={disabled}
              title="Visibility"
              icon={<Eye className="size-4" />}
            />
            <IconButton
              onClick={() =>
                this.setState({
                  showCameraMenu: !this.state.showCameraMenu,
                  showVisibilityMenu: false,
                })
              }
              disabled={disabled}
              title="Camera views"
              icon={<Video className="size-4" />}
            />
            <div className="w-px h-6 bg-white/20 mx-1" />
            <ToolbarButton
              onClick={this.takeSnapshotClick}
              disabled={disabled}
              icon={<Camera className="size-4" />}
              label="Snapshot"
            />
            <ShareModal uuid={this.props.uuid} />
            <div className="w-px h-6 bg-white/20 mx-1" />
            <ToolbarButton
              onClick={() => window.open('https://www.hi-levelmezzanines.co.uk/contact/')}
              variant="primary"
              icon={<Phone className="size-4" />}
              label="Contact Us"
            />
          </div>
        </div>
      </div>
    );
  }
}

function ToolbarButton({
  onClick,
  disabled,
  icon,
  label,
  variant = 'default',
}: {
  onClick: () => void;
  disabled?: boolean;
  icon: React.ReactNode;
  label: string;
  variant?: 'default' | 'primary';
}) {
  const base =
    'flex items-center gap-2 rounded-xl px-4 py-2 text-sm font-medium transition-all cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed';
  const variants = {
    default: 'text-white/90 hover:bg-white/10 active:bg-white/15',
    primary: 'bg-brand-red text-white hover:bg-brand-red-hover shadow-lg shadow-brand-red/25',
  };
  return (
    <button onClick={onClick} disabled={disabled} className={`${base} ${variants[variant]}`}>
      {icon}
      <span className="hidden sm:inline">{label}</span>
    </button>
  );
}

function IconButton({
  onClick,
  disabled,
  icon,
  title,
  active,
  className: extraClassName,
}: {
  onClick: () => void;
  disabled?: boolean;
  icon: React.ReactNode;
  title: string;
  active?: boolean;
  className?: string;
}) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      title={title}
      className={`rounded-lg p-2 transition-all cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed ${
        active ? 'bg-white/20 text-white' : 'text-white/90 hover:bg-white/10 active:bg-white/15'
      } ${extraClassName ?? ''}`}
    >
      {icon}
    </button>
  );
}
